import { Box, CircularProgress, Divider, Popover, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import { usePerspective } from "../../contexts/app/provider";
import { GetTransformedRawParquetDocument } from "../../services/perspective_api/__generated__/perspective_server-types";
import { parseParquetData, FullParsedParquetData } from "../../utils/parquet";
import { RunSettings } from "../../services/appsync/types";
import {
  closeUnfulfilledDemandCellPopover,
  useUnfulfilledDemandCellPopoverTarget,
} from "./store";

interface UnfulfilledDemandRow {
  /** "sales" (a channel) or "transfer" (a destination location). */
  source_kind: string;
  source_label: string;
  /** Exact demand this source contributed to the cell's period. */
  demand_units: number;
  demand_share: number | null;
  /** ESTIMATED — demand-share allocation, not an engine figure. */
  estimated_unfulfilled: number | null;
  cell_demand_total: number | null;
  cell_unfulfilled_total: number | null;
  is_estimated: boolean;
  /** Uncapped source count (rows are capped at 12 server-side). */
  total_source_count: number | null;
}

interface FetchEntry {
  loading: boolean;
  error: string | null;
  rows: UnfulfilledDemandRow[] | null;
}

// Module-level cache so reopening a cell never refetches within a session.
// Keyed by run + cell identity; a new run settings object is a new key.
const detailCache = new Map<string, FetchEntry>();

const toNumberOrNull = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v); // arrow Int64 arrives as bigint
  return Number.isNaN(n) ? null : n;
};

function arrowToRows(parsed: FullParsedParquetData): UnfulfilledDemandRow[] {
  return parsed.arrowTable.toArray().map((r: any) => ({
    source_kind: String(r.source_kind ?? ""),
    source_label: String(r.source_label ?? ""),
    demand_units: toNumberOrNull(r.demand_units) ?? 0,
    demand_share: toNumberOrNull(r.demand_share),
    estimated_unfulfilled: toNumberOrNull(r.estimated_unfulfilled),
    cell_demand_total: toNumberOrNull(r.cell_demand_total),
    cell_unfulfilled_total: toNumberOrNull(r.cell_unfulfilled_total),
    is_estimated: Boolean(r.is_estimated),
    total_source_count: toNumberOrNull(r.total_source_count),
  }));
}

const formatQty = (n: number | null): string =>
  n == null ? "—" : n.toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatShare = (s: number | null): string =>
  s == null ? "—" : `${(s * 100).toFixed(s < 0.1 ? 1 : 0)}%`;

const FIELD_LABEL_SX = {
  fontSize: "10.5px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  color: (t: any) => t.palette.atomic.muted,
};

/** Distinguishes a sales channel from a transfer destination at a glance. */
function SourceChip({ kind }: { kind: string }) {
  const isTransfer = kind === "transfer";
  return (
    <Box
      sx={{
        display: "inline-block",
        fontSize: "9.5px",
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        borderRadius: "4px",
        padding: "1px 5px",
        color: (t) => t.palette.atomic.ink,
        backgroundColor: (t) =>
          isTransfer ? t.palette.atomic.orange1 : t.palette.action.selected,
      }}
    >
      {isTransfer ? "transfer" : "sales"}
    </Box>
  );
}

/**
 * Popover breaking one "unfulfilled demand" cell into the demand behind it —
 * sales by channel, plus transfers by destination on hub cells.
 *
 * The per-source split is an ESTIMATE and is labelled as such in the UI. The
 * engine computes unfulfilled demand as a clipped per-period residual after
 * channel and destination are already collapsed, so no per-source figure
 * exists; the transform allocates the cell total by demand share. The demand
 * numbers themselves are exact — see the transform's README.
 *
 * Render-isolation contract: this component (and its module store) is the ONLY
 * thing that re-renders on open/close/data-arrival. It must never feed state
 * back into the matrix table — the clicked-cell outline is applied
 * imperatively to the anchor element for the same reason.
 */
export function UnfulfilledDemandCellPopover({
  runSettings,
}: {
  runSettings: RunSettings | null;
}) {
  const target = useUnfulfilledDemandCellPopoverTarget();
  const { perspectiveApiService } = usePerspective();
  const theme = useTheme();
  const outlineColor = theme.palette.atomic.brandBlue;
  const [entry, setEntry] = useState<FetchEntry>({
    loading: false,
    error: null,
    rows: null,
  });

  // Outline the clicked cell while the popover is open (imperative on purpose
  // — see render-isolation contract above).
  useEffect(() => {
    if (!target) return;
    const el = target.anchorEl;
    const prevOutline = el.style.outline;
    const prevOffset = el.style.outlineOffset;
    el.style.outline = `2px solid ${outlineColor}`;
    el.style.outlineOffset = "-2px";
    return () => {
      el.style.outline = prevOutline;
      el.style.outlineOffset = prevOffset;
    };
  }, [target, outlineColor]);

  useEffect(() => {
    if (!target) return;
    // A late async continuation (parseParquetData can do a network fetch) must
    // not write into a popover that's since re-anchored to another cell.
    let cancelled = false;
    const cacheKey = JSON.stringify([
      runSettings,
      target.sku,
      target.location,
      target.periodStartMs,
      target.dateHierarchy,
    ]);
    // Only completed fetches count as cache hits. A stale {loading:true} entry
    // (popover closed mid-flight) or an error entry must refetch, or reopening
    // would spin forever.
    const cached = detailCache.get(cacheKey);
    if (cached?.rows) {
      setEntry(cached);
      return;
    }
    const loadingEntry: FetchEntry = { loading: true, error: null, rows: null };
    detailCache.set(cacheKey, loadingEntry);
    setEntry(loadingEntry);

    // Failure is latched here and resolved on `complete` — see the handlers.
    let pendingError: string | null = null;
    let sawData = false;

    const client = perspectiveApiService.getClient();
    const sub = client
      .subscribe({
        query: GetTransformedRawParquetDocument,
        variables: {
          datasetName: "supply_walk_test",
          transformId: "unfulfilled_demand_details",
          folder: "outputs",
          runSettingsJson: runSettings ?? null,
          comparedToRunSettingsJson: null,
          format: "parquet" as const,
          params: {
            sku: target.sku,
            // Omit location for SKU-level cells so the transform aggregates
            // across every location.
            ...(target.location != null ? { location: target.location } : {}),
            period_start: new Date(target.periodStartMs).toISOString(),
            date_hierarchy: target.dateHierarchy,
          },
        },
      })
      .subscribe({
        next: async (res: any) => {
          const payload = res?.data?.getTransformedRawParquet;
          const parquetData = payload?.parquetData;
          // Latch a failure rather than rendering it straight away. The
          // server's FIRST emit reports cached state, which can be a previous
          // attempt's failure that the fresh run then supersedes — rendering
          // it immediately flashes an error a moment before the data lands.
          // `complete` decides, once we know nothing better is coming.
          if (payload?.status === "FAILED" || payload?.errorMessage) {
            pendingError = String(
              payload?.errorMessage || "Transformation failed"
            );
            return;
          }
          // Still in flight (LOADING, or a progress message with no payload).
          if (!parquetData || typeof parquetData !== "string") return;
          // Set before awaiting: `complete` can fire while the parse is still
          // in flight, and must not mistake that for "no data arrived".
          sawData = true;
          pendingError = null;
          try {
            const parsed = await parseParquetData(
              parquetData,
              !parquetData.startsWith("http")
            );
            const rows = arrowToRows(parsed as FullParsedParquetData);
            const done: FetchEntry = { loading: false, error: null, rows };
            detailCache.set(cacheKey, done);
            if (!cancelled) setEntry(done);
          } catch (e: any) {
            const failed: FetchEntry = {
              loading: false,
              error: String(e?.message || e),
              rows: null,
            };
            detailCache.set(cacheKey, failed);
            if (!cancelled) setEntry(failed);
          }
        },
        error: (err: any) => {
          const failed: FetchEntry = {
            loading: false,
            error: String(err?.message || err),
            rows: null,
          };
          detailCache.set(cacheKey, failed);
          if (!cancelled) setEntry(failed);
        },
        complete: () => {
          // The stream ended. If data arrived, the handler above already
          // rendered it. Otherwise surface the latched failure — or say the
          // request returned nothing, so a silent server-side failure can
          // never leave the popover spinning forever.
          if (sawData) return;
          const failed: FetchEntry = {
            loading: false,
            error: pendingError ?? "No data returned for this cell",
            rows: null,
          };
          detailCache.set(cacheKey, failed);
          if (!cancelled) setEntry(failed);
        },
      });
    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
  }, [target, runSettings, perspectiveApiService]);

  if (!target) return null;

  return (
    <Popover
      open
      anchorEl={target.anchorEl}
      onClose={closeUnfulfilledDemandCellPopover}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: {
          sx: {
            padding: "12px 14px",
            minWidth: 260,
            maxWidth: 340,
          },
        },
      }}
    >
      <PopoverBody entry={entry} />
    </Popover>
  );
}

function PopoverBody({ entry }: { entry: FetchEntry }) {
  if (entry.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "8px" }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (entry.error) {
    return (
      <Box sx={{ fontSize: "12px", color: (t) => t.palette.atomic.red1 }}>
        Couldn't load demand sources: {entry.error}
      </Box>
    );
  }
  const rows = entry.rows ?? [];
  if (rows.length === 0) {
    return (
      <Box sx={{ fontSize: "12px", color: (t) => t.palette.atomic.muted }}>
        No demand sources available for this cell.
      </Box>
    );
  }

  const cellUnfulfilled = rows[0]?.cell_unfulfilled_total ?? null;
  const cellDemand = rows[0]?.cell_demand_total ?? null;
  const totalCount = rows[0]?.total_source_count ?? rows.length;

  return (
    <Box>
      <Box sx={{ mb: "8px" }}>
        <Box sx={FIELD_LABEL_SX}>Unfulfilled demand</Box>
        <Box
          sx={{
            fontSize: "16px",
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
            color: (t) => t.palette.atomic.red1,
          }}
        >
          {formatQty(cellUnfulfilled)}
        </Box>
        <Box sx={{ fontSize: "11px", color: (t) => t.palette.atomic.muted }}>
          of {formatQty(cellDemand)} total demand
        </Box>
      </Box>

      {/*
        The engine has no per-source unfulfilled figure — it computes a clipped
        per-period residual after channel/destination are collapsed. This split
        is allocated by demand share, so say so rather than implying precision.
      */}
      <Box
        sx={{
          fontSize: "10.5px",
          fontWeight: 600,
          color: (t) => t.palette.atomic.muted,
          backgroundColor: (t) => t.palette.action.hover,
          borderRadius: "4px",
          padding: "5px 7px",
          mb: "8px",
          lineHeight: 1.35,
        }}
      >
        Estimated split — allocated by each source's share of demand. Demand
        figures are exact.
      </Box>

      {totalCount > rows.length && (
        <Box
          sx={{
            fontSize: "11px",
            fontWeight: 600,
            color: (t) => t.palette.atomic.muted,
            mb: "6px",
          }}
        >
          Showing top {rows.length} of {totalCount} sources
        </Box>
      )}

      <Box sx={{ maxHeight: 320, overflowY: "auto", pr: "2px" }}>
        {rows.map((row, i) => (
          <Box key={`${row.source_kind}-${row.source_label}-${i}`}>
            {i > 0 && <Divider sx={{ my: "8px" }} />}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                mb: "4px",
              }}
            >
              <SourceChip kind={row.source_kind} />
              <Box
                sx={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: (t) => t.palette.atomic.ink,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.source_label}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: "16px",
                py: "2px",
              }}
            >
              <Box sx={FIELD_LABEL_SX}>Est. unfulfilled</Box>
              <Box
                sx={{
                  fontSize: "13px",
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                  color: (t) => t.palette.atomic.red1,
                }}
              >
                {formatQty(row.estimated_unfulfilled)}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: "16px",
                py: "2px",
              }}
            >
              <Box sx={FIELD_LABEL_SX}>Demand</Box>
              <Box
                sx={{
                  fontSize: "12px",
                  fontVariantNumeric: "tabular-nums",
                  color: (t) => t.palette.atomic.ink,
                }}
              >
                {formatQty(row.demand_units)} ({formatShare(row.demand_share)})
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
